import { useState, useCallback, useRef } from 'react';
import { Card, Badge, Space, Typography, Spin, message, Dropdown, Modal } from "antd";
import { FolderOutlined, FileOutlined, DownloadOutlined, MoreOutlined } from "@ant-design/icons";
import FileManagerPOA from '../Poa/FileManagerPOA';
import { fetchPoaData } from '../../services/poa/get_poa';
import { downloadFolder } from '../../services/proyectoEstructurado/proyectoEstructuracionTreeService';

const { Text } = Typography;

export function FolderCard({
  codigo_tarea,
  filtro,
  item,
  onClick,
  isNavigable = true,
  onUpdateFileCount,
  onFileUpload,
  onRefresh,
}) {
  const canNavigate = isNavigable && item.children && item.children.length > 0;

  const [openModalVisitasEcas, setOpenModalVisitasEcas] = useState(false);
  const [itemParams, setItemParams] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ✅ useRef en lugar de useState: evita el closure stale en hideModalVisitasEcas.
  // Con useState, useCallback capturaba filesUploaded=false del render anterior.
  // Con useRef, siempre leemos el valor actual en el momento del cierre.
  const filesUploadedRef = useRef(false);

  // Callback que FileManagerPOA llama cada vez que sube/elimina un archivo
  const handleFileUploaded = useCallback(() => {
    filesUploadedRef.current = true;
  }, []);

  const buildRutaFromCodigoTarea = (input) => {
    const codigo = input?.codigo_tarea ?? input;
    if (!codigo) return null;
    const { codActividad, codigo_tarea: codigoTarea } = codigo;
    if (!codActividad || !codigoTarea) return null;
    const actividad = `actividad_${codActividad.replace('.', '')}`;
    const tarea = `tarea_${codigoTarea.replace(/\./g, '')}`;
    return `${actividad}/${tarea}/`;
  };

  const buildRutaFromFiltro = (filtro) => {
    if (!filtro) return null;
    const { actividad, tarea } = filtro;
    if (!actividad || !tarea) return null;
    const actividadRuta = `actividad_${actividad.replace('.', '')}`;
    const tareaRuta = `tarea_${tarea}`;
    return `${actividadRuta}/${tareaRuta}/`;
  };

  const handleClick = () => {
    if (canNavigate) {
      onClick(item);
    } else {
      const banderaCarpetasArchivos = item.dirCount === 0 && item.fileCount === 0;
      let fullRuta = null;
      let codigo_tareaOid = null;

      if (!codigo_tarea) {
        fullRuta = buildRutaFromFiltro(filtro);
        codigo_tareaOid = filtro?.tarea_codigo ?? null;
      } else {
        codigo_tareaOid = codigo_tarea?.codigo_tarea?.codigo_tarea ?? null;
        fullRuta = buildRutaFromCodigoTarea(codigo_tarea);
      }

      const cleanedPath = item.path.replace(fullRuta, '');
      procesarMpoa(codigo_tareaOid, cleanedPath, banderaCarpetasArchivos);
    }
  };

  const handleDownload = async (e) => {
    if (e) e.stopPropagation();
    if (item.type !== "folder") return;

    setDownloading(true);
    try {
      const response = await downloadFolder(item.path);
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.name}.zip`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      message.success(`Descarga de "${item.name}" iniciada`);
    } catch (error) {
      console.error('Error al descargar la carpeta:', error);
      message.error('Error al descargar la carpeta');
    } finally {
      setDownloading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Al cerrar FileManagerPOA:
  //  - Si hubo subidas/eliminaciones → llama onRefresh (que en
  //    FolderNavigator invalida cache y hace refresh completo)
  //  - Resetea el flag filesUploaded
  // ─────────────────────────────────────────────────────────────────────
  const hideModalVisitasEcas = useCallback(() => {
    setOpenModalVisitasEcas(false);
    setItemParams(null);

    // ✅ Lee el ref directamente — siempre el valor actual, nunca stale
    if (filesUploadedRef.current) {
      filesUploadedRef.current = false;
      if (onRefresh) {
        onRefresh(null, item.path);
      }
    }
  // item.path y onRefresh son estables; no necesitamos filesUploaded en deps
  }, [onRefresh, item.path]);

  const procesarMpoa = async (Mpoa, ruta, banderaCarpetasArchivos) => {
    if (!Mpoa) {
      setOpenModalVisitasEcas(false);
      setItemParams(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const manejarActividadEspecial = (actividad) => {
      switch (actividad) {
        case '2.2.1.1':
        case '2.2.1.2':
        case '2.2.1.3':
          return '2.2.1';
        default:
          return actividad;
      }
    };

    const actividadBase = manejarActividadEspecial(Mpoa);

    try {
      const dataCompleta = await fetchPoaData();
      const dataPoa = dataCompleta.dataPoa;

      const findNodePath = (node, path = []) => {
        path.push(node);
        if (node.codigo_tarea === actividadBase && !['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
          return path;
        }
        if (['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
          if (node.children) {
            for (let child of node.children) {
              if (Mpoa === '2.2.1.2' && child.key === 'indicador_33') return [...path, child];
              if (Mpoa === '2.2.1.3' && child.key === 'indicador_34') return [...path, child];
            }
          }
        }
        if (node.children) {
          for (let child of node.children) {
            const result = findNodePath(child, [...path]);
            if (result) return result;
          }
        }
        return null;
      };

      let matchedPath = null;
      for (let nodo of dataPoa) {
        matchedPath = findNodePath(nodo);
        if (matchedPath) break;
      }

      if (matchedPath) {
        const initialData = extractData(matchedPath);
        const actividadData = initialData[0].actividadData;
        const tareaEncontrada = actividadData.find((item) => {
          if (['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
            return (
              item.codigo_tarea === actividadBase &&
              ((Mpoa === '2.2.1.2' && item.key === 'indicador_33') ||
                (Mpoa === '2.2.1.3' && item.key === 'indicador_34'))
            );
          }
          return item.codigo_tarea === actividadBase;
        });

        if (tareaEncontrada) {
          tareaEncontrada.rutaEstructuracion = ruta;
          tareaEncontrada.banderaCarpetasArchivos = banderaCarpetasArchivos;
          setItemParams(tareaEncontrada);
          setOpenModalVisitasEcas(true);
        } else {
          setOpenModalVisitasEcas(false);
          setItemParams(null);
        }
      } else {
        console.warn("No se encontró ningún nodo coincidente");
      }
    } catch (error) {
      console.error("Error al obtener los datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const extractData = (children) => {
    return children.flatMap(child => {
      if (child.children && child.tipo === 'Actividad') {
        const actividadData = child.children.flatMap(grandchild => {
          if (grandchild.children && grandchild.tipo === 'Tarea') {
            return grandchild.children.flatMap(greatGrandchild => {
              if (greatGrandchild.tipo === 'Indicador') {
                const fecha = greatGrandchild.meta_e?.[0]?.fecha;
                const fechaFormateada = fecha
                  ? new Date(fecha).toLocaleDateString('es-ES')
                  : '-';
                const medioV = greatGrandchild.medioVerificacion || null;
                return {
                  key: greatGrandchild.key,
                  codigo_tarea: grandchild.codigo_tarea,
                  codActividad: grandchild.codActividad,
                  nameActividad: child.actividad,
                  tarea: grandchild.tarea,
                  indicador: greatGrandchild.title,
                  medioV,
                  fechas: fechaFormateada,
                  responsables: 'N/A',
                  soportes: 'N/A',
                  datoIndicador: greatGrandchild,
                };
              }
              return [];
            });
          }
          return [];
        });
        return [{ actividadTitle: child.title, actividad: child.actividad, actividadData }];
      }
      return [];
    });
  };

  return (
    <>
      <Card
        onClick={handleClick}
        style={{
          height: 110,
          padding: 16,
          cursor: canNavigate ? "pointer" : "default",
          display: "flex",
          flexDirection: "column",
          borderColor: canNavigate ? "#43a1f8ff" : "#f0f0f0",
          position: 'relative',
        }}
      >
        {item.type === "folder" && (
          <Dropdown
            menu={{
              items: [
                {
                  key: "download",
                  icon: <DownloadOutlined />,
                  label: 'Descargar carpeta',
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    handleDownload(e.domEvent);
                  },
                },
              ],
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <span
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                zIndex: 1,
                color: (item.dirCount === 0 && item.fileCount === 0) ? '#ccc' : '#1890ff',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined style={{ fontSize: '16px' }} />
            </span>
          </Dropdown>
        )}

        <Space align="start" style={{ width: "100%" }}>
          {item.type === "folder" ? (
            <FolderOutlined style={{ fontSize: 32 }} />
          ) : (
            <FileOutlined style={{ fontSize: 32, color: "#8c8c8c" }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                display: "block",
                maxWidth: "95%",
              }}
            >
              {/* viewName ya trae el prefijo visual cuando aplica */}
              {item.viewName || item.name}
            </Text>

            <Space style={{ paddingTop: 10, paddingRight: 5 }}>
              <Badge
                count={`${item.dirCount.toLocaleString()} carpetas`}
                style={{ backgroundColor: "#fac840ff", color: "#fff" }}
              />
            </Space>

            <Space style={{ paddingTop: 10, paddingLeft: 5 }}>
              <Badge
                count={`${item.fileCount.toLocaleString()} archivos`}
                style={{
                  backgroundColor: "#52c41a",
                  color: "#fff",
                  opacity: item.fileCount === 0 ? 0.5 : 1,
                }}
              />
            </Space>

            {item.typeS && (
              <Badge
                count={item.typeS}
                style={{ backgroundColor: "#fff", border: "1px solid #d9d9d9", color: "#000" }}
              />
            )}
          </div>
        </Space>
      </Card>

      {/* Modal de descarga con spinner */}
      <Modal
        open={downloading}
        footer={null}
        closable={false}
        destroyOnHidden
        centered
        width={300}
        style={{ textAlign: 'center' }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Descargando carpeta...</div>
      </Modal>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100%", height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex", justifyContent: "center", alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin tip="Cargando..." size="large" />
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          FileManagerPOA recibe onUploaded para notificar cada vez que
          sube o elimina un archivo, sin necesidad de tocar FileManagerPOA
          internamente. La señal se recoge en handleFileUploaded.
      ───────────────────────────────────────────────────────────────── */}
      {openModalVisitasEcas && !loading && (
        <FileManagerPOA
          codigo_tarea={{ codigo_tarea: itemParams }}
          onClose={hideModalVisitasEcas}
          onUploaded={handleFileUploaded}
        />
      )}
    </>
  );
}